import { FC } from 'react'
import type { CrewDerivedKpis } from './types'
import type { ExpiryRow } from './crewDashboardData'

interface Props {
  loading: boolean
  kpis: CrewDerivedKpis
  onKpiClick: (kpi: string) => void
  crew: any[]
  signOff: any[]
  appraisals: any[]
  vessels: any[]
  expiry: ExpiryRow[] | null
  expiryError: boolean
  expiryProgress?: {done: number; total: number}
  failedSources: string[]
}

const counts = (values: string[]) => Object.entries(values.reduce<Record<string, number>>((acc, v) => {
  acc[v || 'Unknown'] = (acc[v || 'Unknown'] || 0) + 1
  return acc
}, {})).sort((a, b) => b[1] - a[1]).slice(0, 8)

const KpiCards: FC<Props> = ({loading, kpis, onKpiClick, crew, signOff, appraisals, vessels, expiry, expiryError, failedSources}) => {
  const cards = [
    {key: 'totalCrew', label: 'Total Crew', sub: 'All crew members', value: kpis.totalCrew, data: counts(crew.map(c => c.department)), color: '#3b82f6', hint: 'Crew by department'},
    {key: 'pendingAppraisals', label: 'Submitted Appraisals', sub: 'Awaiting review', value: kpis.pendingAppraisals, data: counts(appraisals.filter(a => a.workflowStatus === 'SUBMITTED').map(a => a.appraisalType)), color: '#10b981', hint: 'Submitted appraisals by type'},
    {key: 'pendingApprovals', label: 'Pending Approvals', sub: 'Awaiting review', value: kpis.pendingApprovals, data: counts(crew.filter(c => !c.approvalStatus || c.approvalStatus === 'PENDING').map(c => c.department)), color: '#f59e0b', hint: 'Pending crew by department'},
    {key: 'expiringCerts', label: 'Certifications', sub: 'Expired or due within 30 days', value: expiryError ? 'Unavailable' : expiry === null ? 'Loading' : kpis.expiringCerts, data: counts((expiry || []).map(e => e.document)), color: '#ef4444', hint: 'Documents by type'},
    {key: 'signedOff', label: 'Signed Off / Onboard', sub: 'Sign-on and sign-off records', value: signOff.filter(r => r.status === 'SIGNED_OFF' || r.status === 'SIGNED_ON').length, data: counts(signOff.filter(r => r.status === 'SIGNED_OFF' || r.status === 'SIGNED_ON').map(r => r.status)), color: '#8b5cf6', hint: 'Records by status'},
    {key: 'vessels', label: 'Vessels', sub: 'Accessible vessels', value: kpis.vessels, data: counts(vessels.map(v => v.vesselType)), color: '#14b8a6', hint: 'Vessels by type'},
  ]
  return <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
    {cards.map(card => {
      const source = ({totalCrew: 'crew', pendingApprovals: 'crew', pendingAppraisals: 'appraisals', signedOff: 'signOff', vessels: 'vessels'} as Record<string, string>)[card.key]
      const unavailable = !!source && failedSources.includes(source)
      const max = Math.max(...card.data.map(([, value]) => value), 1)
      const plotData: [string, number][] = card.data.length === 0 ? [['No records', 0], ['No records', 0]] : card.data.length === 1 ? [card.data[0], card.data[0]] : card.data
      return <button key={card.key} disabled={unavailable} onClick={() => onKpiClick(card.key)} className='text-left flex flex-col bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:shadow-md'>
        <span className='text-[10px] text-slate-500'>{card.label}</span>
        <span className='text-2xl font-bold text-slate-900'>{loading ? '—' : unavailable ? 'Unavailable' : card.value}</span>
        <span className='text-[10px] mt-1' style={{color: card.color}}>{card.sub}</span>
        <span className='block mt-2 h-10 w-full rounded-lg overflow-hidden' style={{backgroundColor: `${card.color}14`}}>
          {!unavailable && !(card.key === 'expiringCerts' && !expiry) && <svg role='img' aria-label={`${card.hint}: ${card.data.map(([name, value]) => `${name}: ${value}`).join(', ') || 'no records'}`} viewBox='0 0 100 32' preserveAspectRatio='none' className='w-full h-full'>
            <title>{`${card.hint} (categories, not a time trend): ${card.data.map(([name, value]) => `${name}: ${value}`).join(', ') || 'no records'}`}</title>
            <polyline points={plotData.map(([, value], index) => `${index / (plotData.length - 1) * 100},${27 - value / max * 23}`).join(' ')} fill='none' stroke={card.color} strokeWidth='1.5' vectorEffect='non-scaling-stroke' />
          </svg>}
        </span>
      </button>
    })}
  </div>
}

export default KpiCards
